import Link from "next/link";
import { ReactElement, useCallback, useState } from "react";
import PrimaryButton from "../components/buttons/primary";
import AuthCode from "../components/input/authCode";
import TextInput from "../components/input/text";
import AuthLayout from "../components/layout/auth";
import AuthTitle from "../components/headers/authTitle";
import { useAuth } from "../hooks/auth";
import validator from "validator";
import ErrorCallout from "../components/callouts/error";
import NeutralButton from "../components/buttons/neutral";
import { GetServerSideProps } from "next/types";
import Unprotected from "../server/unprotected";

export default function ForgotPassword() {
  const { forgotPassword, forgotPasswordSubmitAndSignIn } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [forgotPasswordFieldsRequired, setForgotPasswordFieldsRequired] =
    useState(false);
  const [resetPasswordFieldsRequired, setResetPasswordFieldsRequired] =
    useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(
    null
  );
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(
    null
  );

  const handleForgot = useCallback(async () => {
    if (!validator.isEmail(email)) {
      setForgotPasswordFieldsRequired(true);
      return;
    }
    try {
      await forgotPassword(email);
      setNeedsConfirmation(true);
    } catch (error) {
      if (error instanceof Error) {
        console.log(error);
        setForgotPasswordError(error.message);
      }
    }
  }, [email, forgotPassword, setNeedsConfirmation]);

  const resendCode = useCallback(async () => {
    setCode("");
    setPassword("");
    await forgotPassword(email);
    email;
  }, [email, forgotPassword, setCode]);

  const handleReset = useCallback(async () => {
    if (password.length < 7) {
      setResetPasswordFieldsRequired(true);
      return;
    }
    try {
      await forgotPasswordSubmitAndSignIn(email, code, password);
    } catch (error) {
      if (error instanceof Error) {
        setResetPasswordError(error.message);
        setResetPasswordFieldsRequired(false);
        setCode("");
        setPassword("");
      }
    }
  }, [email, code, password, forgotPasswordSubmitAndSignIn]);

  if (needsConfirmation) {
    return (
      <>
        {resetPasswordError ? (
          <div className="mb-10">
            <ErrorCallout subTitle={resetPasswordError}></ErrorCallout>
          </div>
        ) : null}
        <AuthTitle
          title="Reset your password"
          subTitle="Enter your code and new password."
        ></AuthTitle>
        <form className="mb-6">
          <div className="mb-6">
            <AuthCode value={code} onChange={setCode} />
          </div>
          <div className="mb-6">
            <TextInput
              id="password"
              label="New password"
              autoComplete="new-password"
              type="password"
              value={password}
              onChange={setPassword}
              required={resetPasswordFieldsRequired}
            ></TextInput>
          </div>
          <PrimaryButton className="w-full" onClick={handleReset}>
            <p>Reset</p>
          </PrimaryButton>
        </form>
        <div className="mb-6">
          <NeutralButton
            onClick={resendCode}
            text="Resend code"
          ></NeutralButton>
        </div>
        <div>
          <Link href="/signin">
            <a className="text-sm text-primary-base">
              {"Remembered your password? Back to sign in"}
            </a>
          </Link>
        </div>
      </>
    );
  } else {
    return (
      <>
        {forgotPasswordError ? (
          <div className="mb-10">
            <ErrorCallout subTitle={forgotPasswordError}></ErrorCallout>
          </div>
        ) : null}

        <form className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-vendrive-500 focus:border-vendrive-500 sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={forgotPasswordFieldsRequired}
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <div className="text-sm">
              <Link href="/signin">
                <a className="font-medium text-vendrive-600 hover:text-vendrive-500">
                  Remember your password?
                </a>
              </Link>
            </div>
          </div>

          <div>
            <PrimaryButton
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-vendrive-600 hover:bg-vendrive-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vendrive-500"
              onClick={handleForgot}
            >
              Send
            </PrimaryButton>
          </div>
        </form>
      </>
    );
  }
}

ForgotPassword.getLayout = function getLayout(page: ReactElement) {
  return (
    <AuthLayout
      title="Reset password"
      heading="Reset your password"
      subHeading="Enter your email and we'll send you a reset code"
    >
      {page}
    </AuthLayout>
  );
};

export const getServerSideProps: GetServerSideProps = Unprotected();
